import React from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { FiImage, FiArrowUpRight } from "react-icons/fi";
import logo from "../../Image/Client/logo1.png";

/* Static navigation links for the wallpaper section footer */
const LINKS = [
    { label: "Home", path: "/" },
    { label: "Wallpapers", path: "/wallpaper" },
    { label: "About Us", path: "/about" },
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms & Conditions", path: "/terms" },
    { label: "Contact Us", path: "/contact" },
];

/* Lightweight, static footer used only on the wallpaper pages — gives users
   a clear way to navigate the wallpaper section (and back to the main site). */
const WallpaperFooter = () => {
    const navigate = useNavigate();
    const year = new Date().getFullYear();

    return (
        <footer className='w-full mt-12 md:mt-16'>
            <div
                className='relative w-full overflow-hidden'
                style={{
                    background: "rgba(255,255,255,0.025)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                }}
            >
                {/* glow */}
                <div
                    aria-hidden
                    className='absolute -top-16 left-[12%] w-72 h-72 rounded-full opacity-20 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-7xl px-4 sm:px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10'>
                    {/* brand */}
                    <div className='md:col-span-5'>
                        <motion.img
                            src={logo}
                            alt='PicPromptify'
                            whileHover={{ scale: 1.04 }}
                            onClick={() => navigate("/")}
                            className='w-32 md:w-40 h-auto cursor-pointer'
                        />
                        <p className='text-client-font/65 text-sm md:text-base leading-relaxed mt-4 max-w-sm'>
                            Free HD phone wallpapers — browse by category and download
                            in full 1080×1920 quality.
                        </p>

                        <motion.button
                            whileHover={{ y: -2, scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => navigate("/wallpaper")}
                            className='mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-xs md:text-sm'
                            style={{
                                background: "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                boxShadow:
                                    "0 12px 28px -8px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                            }}
                        >
                            <FiImage className='text-base' />
                            Browse Wallpapers
                        </motion.button>
                    </div>

                    {/* quick links */}
                    <div className='md:col-span-7'>
                        <div className='flex items-center gap-2 mb-4'>
                            <div
                                className='w-1 h-4 rounded-full'
                                style={{
                                    background:
                                        "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                }}
                            />
                            <h4 className='text-client-font font-semibold text-sm tracking-wide uppercase'>
                                Quick Links
                            </h4>
                        </div>
                        <ul className='grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2.5'>
                            {LINKS.map((link) => (
                                <li key={link.path}>
                                    <motion.button
                                        onClick={() => navigate(link.path)}
                                        whileHover={{ x: 6 }}
                                        className='group text-client-font/65 hover:text-client-font text-sm md:text-base inline-flex items-center gap-1.5 transition-colors'
                                    >
                                        <span className='w-1 h-1 rounded-full bg-client-font/30 group-hover:bg-client-primary transition-colors' />
                                        {link.label}
                                    </motion.button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* bottom bar */}
                <div
                    className='w-full border-t border-white/10'
                    style={{ background: "rgba(255,255,255,0.02)" }}
                >
                    <div className='mx-auto max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-2 px-4 sm:px-6 md:px-10 py-5'>
                        <p className='text-client-font/45 text-[11px] md:text-xs tracking-wide'>
                            © {year} PicPromptify. All rights reserved.
                        </p>
                        <button
                            onClick={() => navigate("/contact")}
                            className='text-client-font/45 hover:text-client-font text-[11px] md:text-xs inline-flex items-center gap-1 transition-colors'
                        >
                            Contact Us
                            <FiArrowUpRight />
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default WallpaperFooter;
